import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { getDataSourceDisplayMemberValue } from './FieldEditorFunctions';
import { Typeahead, AsyncTypeahead } from 'react-bootstrap-typeahead';

export interface AutoCompleteProps {
    /** The name of the field. */
    field: string;
    /** The label for the editor. */
    label?: string;
    /** Is this field required? */
    isRequired?: boolean;
    /** Is the editing enabled? */
    isEnabled?: boolean;
    /** The placeholder for the field value. */
    placeholder?: string;
    /** The datasource for the autocomplete. If loadOptions is provided, this will be ignored after the initial load. */
    dataSource?: { [index: string]: any }[];
    /** The datasource object's property that is human readable. */
    dataSourceDisplayMember?: string;
    /** The datasource object's property that is used as the real value for the field. */
    dataSourceValueMember?: string;
    /** Is the input invalid? */
    isInvalid?: boolean;
    /** The value of the field. */
    value?: any;
    /** CSS class for the control. */
    className?: string;
    /** Fired when the field value changes in the editor. */
    onValueChanged?: (field: string, value: any) => void;
    /** Async function to load options based on input. 
     * The options need to have the fields for dataSourceValueMember and dataSourceDisplayMember. 
     * No need to set the dataSource property if this is provided. Actually, it will be ignored.
     */
    loadOptions?: (inputValue: string) => Promise<any[] | undefined>;
}

/**
 * Component: Bootstrap-based Autocomplete using react-bootstrap-typeahead
 */
const AutoComplete: FunctionComponent<AutoCompleteProps> = (props) =>
{
    const isEnabled = props.isEnabled ?? true;
    const dataSourceValueMember = props.dataSourceValueMember ?? 'value';
    const dataSourceDisplayMember = props.dataSourceDisplayMember ?? 'display';

    // Local raw options state (used only for async mode)
    const [options, setOptions] = useState<any[]>(props.dataSource ?? []);
    const [loading, setLoading] = useState(false);

    // Keep local options in sync with incoming dataSource for static use
    useEffect(() => { if (!props.loadOptions) setOptions(props.dataSource ?? []); }, [props.dataSource, props.loadOptions]);

    // Build raw options (source of truth) depending on mode
    const rawOptions = props.loadOptions ? options : (props.dataSource ?? []);

    // Map of id -> raw option for reverse lookup
    const rawById = useMemo(() =>
    {
        const map = new Map<any, any>();
        for (const o of rawOptions)
        {
            if (o && o[dataSourceValueMember] != null) map.set(o[dataSourceValueMember], o);
        }
        return map;
    }, [rawOptions, dataSourceValueMember]);

    // Sanitize an option to avoid circular references inside RBT comparisons
    const toUiOption = (o: any) =>
    ({
        [dataSourceValueMember]: o?.[dataSourceValueMember],
        __label: String(getDataSourceDisplayMemberValue(o, dataSourceDisplayMember) ?? ''),
    });

    // UI-facing options without circular refs
    const uiOptions = useMemo(() => rawOptions.map(toUiOption), [rawOptions, dataSourceValueMember, dataSourceDisplayMember]);

    // Build selected array from `value` (supports passing object or id)
    const selected = useMemo(() =>
    {
        if (props.value == null) return [] as any[];

        // Accept both object and scalar id
        const id = typeof props.value === 'object' ? props.value?.[dataSourceValueMember] : props.value;
        const raw = rawById.get(id);
        if (raw) return [toUiOption(raw)];

        // Fallback: try to build label from provided value object
        const label = typeof props.value === 'object'
            ? String(getDataSourceDisplayMemberValue(props.value, dataSourceDisplayMember) ?? '')
            : String(id ?? '');
        return [{ [dataSourceValueMember]: id, __label: label }];
    }, [props.value, rawById, dataSourceValueMember, dataSourceDisplayMember]);

    const labelKey = (option: any) => String(option?.__label ?? getDataSourceDisplayMemberValue(option, dataSourceDisplayMember));

    const commonProps = {
        id: `${props.field}-autocomplete`,
        clearButton: true,
        disabled: !isEnabled,
        isInvalid: props.isInvalid,
        labelKey,
        onChange: (sel: any[]) =>
        {
            const ui = sel && sel.length ? sel[0] : null;
            if (!ui) return props.onValueChanged?.(props.field, null);
            const id = ui?.[dataSourceValueMember];
            const raw = rawById.get(id);
            props.onValueChanged?.(props.field, raw ?? null);
        },
        placeholder: props.placeholder,
        selected,
        minLength: 0,
        inputProps: {
            name: props.field,
            required: props.isRequired,
            'aria-label': props.label,
            className: `${props.className ?? 'field-editor-input'}`,
        },
    } as const;

    if (props.loadOptions)
    {
        // Async search mode
        const handleSearch = async (query: string) =>
        {
            setLoading(true);
            try
            {
                const newOptions = await props.loadOptions!(query);
                setOptions(newOptions ?? []);
            }
            catch (err)
            {
                console.error('Error loading autocomplete options:', err);
                setOptions([]);
            }
            finally { setLoading(false); }
        };

        return (
            <AsyncTypeahead
                {...commonProps}
                isLoading={loading}
                onSearch={handleSearch}
                options={uiOptions}
                filterBy={() => true} // rely on server-side filtering
            />
        );
    }

    // Static options mode
    return (
        <Typeahead
            {...commonProps}
            options={uiOptions}
        />
    );
}

export default AutoComplete;
